<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FormsAPI from 'dashboard/api/forms';

const props = defineProps({
  formId: { type: [String, Number], default: null },
});

const { t } = useI18n();

const isLoading = ref(true);
const data = ref(null);

const cards = computed(() => {
  if (!data.value) return [];
  return [
    { key: 'VIEWS', value: data.value.views },
    { key: 'STARTS', value: data.value.starts },
    { key: 'SUBMITS', value: data.value.submits },
    { key: 'COMPLETION', value: `${data.value.completion_rate}%` },
  ];
});

// Barras do funil relativas ao passo mais alcançado (normalmente o 1º), pra
// deixar visível a queda de um passo pro outro (drop-off).
const funnel = computed(() => {
  const steps = data.value?.step_funnel || [];
  const max = Math.max(1, ...steps.map(s => s.completed));
  return steps.map(step => ({
    ...step,
    percent: Math.round((step.completed / max) * 100),
  }));
});

const hasData = computed(() => (data.value?.views || 0) > 0);

const load = async () => {
  if (!props.formId) {
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  try {
    const response = await FormsAPI.analytics(props.formId);
    data.value = response.data;
  } catch (error) {
    // Deixa `data` como null — o template já degrada pro estado vazio
    // (EMPTY) quando não há dados, sem precisar de um estado de erro à parte.
    data.value = null;
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.ANALYTICS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.ANALYTICS.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="!formId" class="text-sm text-n-slate-11">
      {{ t('FORMS_MGMT.EDITOR.ANALYTICS.SAVE_FIRST') }}
    </div>

    <div v-else-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('FORMS_MGMT.EDITOR.ANALYTICS.LOADING') }}
    </div>

    <template v-else>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div
          v-for="card in cards"
          :key="card.key"
          class="flex flex-col gap-1 p-4 border border-n-weak rounded-lg bg-n-alpha-1"
        >
          <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ card.value }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t(`FORMS_MGMT.EDITOR.ANALYTICS.CARDS.${card.key}`) }}
          </span>
        </div>
        <div
          v-if="data?.average_score != null"
          class="flex flex-col gap-1 p-4 border border-n-weak rounded-lg bg-n-alpha-1"
        >
          <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ data.average_score }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.ANALYTICS.AVERAGE_SCORE') }}
          </span>
        </div>
      </div>

      <div v-if="hasData" class="flex flex-col gap-3 mt-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.ANALYTICS.FUNNEL_TITLE') }}
        </span>
        <div
          v-for="step in funnel"
          :key="step.step_index"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center justify-between text-sm">
            <span class="text-n-slate-12 truncate">{{ step.label }}</span>
            <span class="text-n-slate-11 tabular-nums">{{
              step.completed
            }}</span>
          </div>
          <div class="h-2 w-full rounded-full overflow-hidden bg-n-alpha-2">
            <div
              class="h-2 rounded-full bg-n-brand transition-all"
              :style="{ width: `${step.percent}%` }"
            />
          </div>
        </div>
      </div>

      <div v-else class="text-sm text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.ANALYTICS.EMPTY') }}
      </div>

      <div
        v-if="(data?.score_distribution || []).length"
        class="flex flex-col gap-2 mt-2"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.ANALYTICS.SCORE_DISTRIBUTION') }}
        </span>
        <div
          v-for="(band, index) in data.score_distribution"
          :key="index"
          data-testid="score-distribution-row"
          class="flex items-center justify-between text-sm text-n-slate-11"
        >
          <span>{{ band.stage }}</span>
          <span class="font-medium text-n-slate-12">{{ band.count }}</span>
        </div>
      </div>

      <!-- F3.4 — conversão por variante do teste A/B -->
      <div
        v-if="(data?.variants || []).length"
        class="flex flex-col gap-2 mt-2"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.ANALYTICS.VARIANTS_TITLE') }}
        </span>
        <div
          v-for="variant in data.variants"
          :key="variant.key"
          class="flex items-center justify-between px-3 py-2 border border-n-weak rounded-lg text-sm"
        >
          <span class="font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.ABTEST.VARIANT', { key: variant.key }) }}
          </span>
          <span class="text-n-slate-11 tabular-nums">
            {{
              t('FORMS_MGMT.EDITOR.ANALYTICS.VARIANT_STATS', {
                views: variant.views,
                submits: variant.submits,
                rate: variant.completion_rate,
              })
            }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>
